/**
 * FormFieldsEditor — manage system + custom fields for one form_key.
 *
 * Renders:
 *   1. The locked system fields row (read-only chips + tooltips).
 *   2. The tenant's custom fields with inline reorder (↑↓), edit, delete.
 *   3. An "Add field" button → opens a side-modal builder.
 *
 * All writes go through the Cloak Write Guard naturally (axios
 * interceptor) so an impersonating Maker will get the confirm modal
 * before any PATCH/DELETE/POST.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Lock, Pencil, Trash2, ChevronUp, ChevronDown,
  Loader2, X, Save, CheckCircle2, Eye, Info,
} from 'lucide-react';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */

const FIELD_TYPES = [
  { id: 'text',        label: 'Text (single line)' },
  { id: 'textarea',    label: 'Text (multi-line)' },
  { id: 'number',      label: 'Number' },
  { id: 'date',        label: 'Date' },
  { id: 'select',      label: 'Select (dropdown)' },
  { id: 'multiselect', label: 'Multi-select' },
  { id: 'checkbox',    label: 'Checkbox (true/false)' },
  { id: 'email',       label: 'Email' },
  { id: 'url',         label: 'URL' },
  { id: 'phone',       label: 'Phone' },
];

function slugify(label) {
  return (label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

// ─────────────────────────────────────────────────────────────────────
//  Field row — single line in the editor table
// ─────────────────────────────────────────────────────────────────────
function SystemFieldRow({ field }) {
  return (
    <div
      data-testid={`field-row-${field.field_key}`}
      data-is-system="1"
      className="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/[0.06] rounded-lg"
    >
      <Lock size={14} className="text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 dark:text-white">{field.label}</span>
          <code className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-white/[0.06] text-slate-600 dark:text-white/55">
            {field.field_key}
          </code>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30 font-bold">
            {field.type}
          </span>
          {field.required && (
            <span
              title={field.is_required_locked ? 'System-required — toggle locked' : ''}
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${
                field.is_required_locked
                  ? 'bg-rose-500/10 text-rose-600 dark:text-rose-300 border border-rose-500/30'
                  : 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30'
              }`}
            >
              {field.is_required_locked ? 'REQUIRED · LOCKED' : 'REQUIRED'}
            </span>
          )}
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-300/40 dark:bg-white/[0.06] text-slate-600 dark:text-white/55 border border-slate-300 dark:border-white/10 font-bold">
            SYSTEM
          </span>
        </div>
        {field.help_text && (
          <div className="text-xs text-slate-500 dark:text-white/55 mt-1">{field.help_text}</div>
        )}
      </div>
    </div>
  );
}

function CustomFieldRow({ field, onEdit, onDelete, onMove, isFirst, isLast }) {
  return (
    <div
      data-testid={`field-row-${field.field_key}`}
      data-is-system="0"
      className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-zinc-900/60 border border-slate-200 dark:border-white/10 rounded-lg hover:border-violet-400/60 dark:hover:border-violet-400/40 transition-colors"
    >
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          aria-label="Move up"
          disabled={isFirst}
          onClick={() => onMove(-1)}
          data-testid={`move-up-${field.field_key}`}
          className="text-slate-400 hover:text-violet-500 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronUp size={14} />
        </button>
        <button
          type="button"
          aria-label="Move down"
          disabled={isLast}
          onClick={() => onMove(1)}
          data-testid={`move-down-${field.field_key}`}
          className="text-slate-400 hover:text-violet-500 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronDown size={14} />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 dark:text-white">{field.label}</span>
          <code className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-white/55">
            {field.field_key}
          </code>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/30 font-bold">
            {field.type}
          </span>
          {field.required && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 font-bold">
              REQUIRED
            </span>
          )}
        </div>
        {field.help_text && (
          <div className="text-xs text-slate-500 dark:text-white/55 mt-1">{field.help_text}</div>
        )}
        {field.options?.length > 0 && (
          <div className="text-[11px] text-slate-500 dark:text-white/55 mt-1">
            {field.options.length} options: {field.options.slice(0, 4).map((o) => o.label).join(', ')}
            {field.options.length > 4 ? '…' : ''}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onEdit(field)}
          data-testid={`edit-field-${field.field_key}`}
          className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-white/[0.06] text-slate-500 hover:text-violet-600"
          aria-label="Edit field"
        >
          <Pencil size={15} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(field)}
          data-testid={`delete-field-${field.field_key}`}
          className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-900/30 text-slate-500 hover:text-rose-600"
          aria-label="Delete field"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Field builder modal — create / edit
// ─────────────────────────────────────────────────────────────────────
function FieldBuilderModal({ field, onClose, onSave, existingKeys }) {
  const isEdit = !!field;
  const [form, setForm] = useState(() => ({
    field_key: field?.field_key || '',
    label: field?.label || '',
    type: field?.type || 'text',
    required: field?.required || false,
    help_text: field?.help_text || '',
    placeholder: field?.placeholder || '',
    options: field?.options || [],
  }));
  const [saving, setSaving] = useState(false);
  const [keyTouched, setKeyTouched] = useState(isEdit);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-suggest field_key from label on create
  const onLabelChange = (v) => {
    setForm((f) => ({
      ...f,
      label: v,
      field_key: keyTouched ? f.field_key : slugify(v),
    }));
  };

  const needsOptions = form.type === 'select' || form.type === 'multiselect';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error('Label is required');
    if (!form.field_key.trim()) return toast.error('Field key is required');
    if (!isEdit && existingKeys.includes(form.field_key)) {
      return toast.error(`Field key "${form.field_key}" is already in use`);
    }
    if (needsOptions && form.options.length === 0) {
      return toast.error('Add at least one option for select / multi-select');
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const addOption = () => set('options', [...form.options, { label: '', value: '' }]);
  const updateOption = (i, k, v) =>
    set('options', form.options.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));
  const removeOption = (i) => set('options', form.options.filter((_, idx) => idx !== i));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="field-builder-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-white/10">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {isEdit ? 'Edit field' : 'Add a custom field'}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Label *
            </label>
            <input
              data-testid="field-label-input"
              type="text"
              value={form.label}
              onChange={(e) => onLabelChange(e.target.value)}
              placeholder="e.g. Compliance Officer"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
              required
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Field key * <span className="font-mono normal-case text-[10px] text-slate-500 ml-1">snake_case</span>
            </label>
            <input
              data-testid="field-key-input"
              type="text"
              value={form.field_key}
              onChange={(e) => { setForm((f) => ({ ...f, field_key: e.target.value })); setKeyTouched(true); }}
              disabled={isEdit}
              placeholder="compliance_officer"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm font-mono focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 disabled:bg-slate-100 disabled:dark:bg-zinc-800 disabled:text-slate-500"
              required
              pattern="^[a-z][a-z0-9_]{1,38}$"
              title="Lowercase letters / digits / underscores, start with a letter"
            />
            {isEdit && (
              <div className="text-[10px] text-slate-500 mt-1">Field key cannot be changed once a field is created.</div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Type
            </label>
            <select
              data-testid="field-type-select"
              value={form.type}
              onChange={(e) => set('type', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="field-required-toggle"
              type="checkbox"
              checked={form.required}
              onChange={(e) => set('required', e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
            />
            <span className="text-sm text-slate-700 dark:text-white/85">Make this field <strong>mandatory</strong></span>
          </label>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Helper text
            </label>
            <input
              data-testid="field-help-input"
              type="text"
              value={form.help_text || ''}
              onChange={(e) => set('help_text', e.target.value)}
              placeholder="Optional explanation shown below the input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
              Placeholder
            </label>
            <input
              type="text"
              value={form.placeholder || ''}
              onChange={(e) => set('placeholder', e.target.value)}
              placeholder="Shown in greyed-out text inside an empty field"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
            />
          </div>

          {needsOptions && (
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-slate-600 dark:text-white/65 mb-1.5 block">
                Dropdown options *
              </label>
              <div className="space-y-2">
                {form.options.map((opt, i) => (
                  // Stable key: combine the row index with the option
                  // value/label so reordering rows doesn't desync state
                  // between adjacent inputs. (Pure index keys cause React
                  // to reuse the wrong DOM node when an option is removed.)
                  <div key={`opt-${i}-${opt.value || opt.label || ''}`} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Label"
                      value={opt.label}
                      onChange={(e) => updateOption(i, 'label', e.target.value)}
                      className="flex-1 px-3 py-1.5 rounded-md border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm focus:border-violet-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="value"
                      value={opt.value}
                      onChange={(e) => updateOption(i, 'value', e.target.value)}
                      className="w-32 px-3 py-1.5 rounded-md border border-slate-300 dark:border-white/10 bg-white dark:bg-zinc-950/50 text-slate-900 dark:text-white text-sm font-mono focus:border-violet-500 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(i)}
                      className="p-1 rounded text-slate-400 hover:text-rose-500"
                      aria-label="Remove option"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addOption}
                  data-testid="add-option-button"
                  className="text-xs uppercase tracking-wider font-bold text-violet-600 dark:text-violet-300 hover:underline flex items-center gap-1"
                >
                  <Plus size={12} /> Add option
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-zinc-950/40">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="save-field-button"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Save changes' : 'Add field'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────
export default function FormFieldsEditor() {
  const { formKey } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const tenantParam = search.get('tenant') || null;

  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const queryParams = tenantParam ? { tenant_id: tenantParam } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/forms/${formKey}/fields`, { params: queryParams });
      setSchema(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to load';
      toast.error(msg);
      if (err?.response?.status === 404) navigate('/dashboard/admin/forms');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, tenantParam]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (payload) => {
    await api.post(`/admin/forms/${formKey}/fields`, payload, { params: queryParams });
    toast.success(`Field "${payload.label}" added`);
    await load();
  };

  const handleEdit = async (payload) => {
    const { field_id } = editingField;
    const patch = { ...payload };
    delete patch.field_key; // immutable
    await api.patch(`/admin/forms/${formKey}/fields/${field_id}`, patch, { params: queryParams });
    toast.success(`Field "${payload.label}" updated`);
    setEditingField(null);
    await load();
  };

  const doDelete = async (field) => {
    try {
      await api.delete(`/admin/forms/${formKey}/fields/${field.field_id}`, { params: queryParams });
      toast.success(`Field "${field.label}" deleted`);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Delete failed';
      toast.error(msg);
    }
  };

  const move = async (field, delta) => {
    const customs = schema.custom_fields;
    const idx = customs.findIndex((f) => f.field_id === field.field_id);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= customs.length) return;
    const reordered = [...customs];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const fieldIds = reordered.map((f) => f.field_id);
    // Optimistic update
    setSchema((s) => ({ ...s, custom_fields: reordered }));
    try {
      await api.post(`/admin/forms/${formKey}/fields/reorder`, { field_ids: fieldIds }, { params: queryParams });
    } catch (err) {
      toast.error('Reorder failed');
      await load();
    }
  };

  if (loading || !schema) {
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="form-fields-editor-loading">
        <div className="flex items-center gap-2 text-slate-500 dark:text-white/55">
          <Loader2 size={16} className="animate-spin" /> Loading form schema…
        </div>
      </div>
    );
  }

  const existingKeys = [
    ...schema.system_fields.map((f) => f.field_key),
    ...schema.custom_fields.map((f) => f.field_key),
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="form-fields-editor-page">
      <header className="mb-6">
        <Link
          to={tenantParam ? `/dashboard/admin/forms?tenant=${encodeURIComponent(tenantParam)}` : '/dashboard/admin/forms'}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-white/55 hover:text-violet-600 mb-3"
          data-testid="back-to-form-designer"
        >
          <ArrowLeft size={14} /> Back to Form Designer
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-900 dark:text-white">
              {schema.label}
            </h1>
            <p className="text-sm text-slate-600 dark:text-white/65 mt-1">
              {schema.description}
            </p>
            <p className="text-[11px] text-slate-500 dark:text-white/55 mt-1 font-mono">
              form_key: {formKey} · tenant: {schema.tenant_id}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowBuilder(true)}
            data-testid="add-custom-field-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm shadow-lg shadow-violet-500/30"
          >
            <Plus size={16} /> Add custom field
          </button>
        </div>
      </header>

      <section className="mb-8" data-testid="system-fields-section">
        <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55">
          <Lock size={12} />
          System fields · always present · cannot be deleted
        </div>
        <div className="space-y-2">
          {schema.system_fields.map((f) => (
            <SystemFieldRow key={f.field_id} field={f} />
          ))}
        </div>
      </section>

      <section data-testid="custom-fields-section">
        <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55">
          <Plus size={12} />
          Custom fields · added by your team
        </div>
        {schema.custom_fields.length === 0 ? (
          <div
            data-testid="custom-fields-empty"
            className="text-center py-10 border border-dashed border-slate-300 dark:border-white/10 rounded-lg text-slate-500 dark:text-white/55 text-sm"
          >
            No custom fields yet. Click <strong>Add custom field</strong> to extend this form
            with your own questions.
          </div>
        ) : (
          <div className="space-y-2">
            {schema.custom_fields.map((f, i) => (
              <CustomFieldRow
                key={f.field_id}
                field={f}
                isFirst={i === 0}
                isLast={i === schema.custom_fields.length - 1}
                onMove={(delta) => move(f, delta)}
                onEdit={(field) => { setEditingField(field); setShowBuilder(true); }}
                onDelete={(field) => setConfirmDelete(field)}
              />
            ))}
          </div>
        )}
      </section>

      {showBuilder && (
        <FieldBuilderModal
          field={editingField}
          existingKeys={existingKeys}
          onClose={() => { setShowBuilder(false); setEditingField(null); }}
          onSave={editingField ? handleEdit : handleCreate}
        />
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="delete-confirm-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-zinc-900 border border-rose-500/40 rounded-xl w-full max-w-md shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-slate-200 dark:border-white/10 flex items-center gap-2">
              <Trash2 size={18} className="text-rose-500" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Delete field?</h2>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-700 dark:text-white/75">
                You&apos;re about to delete the field <strong>{confirmDelete.label}</strong>{' '}
                (<code className="text-xs font-mono">{confirmDelete.field_key}</code>).
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-3 leading-relaxed">
                Any values previously entered into this field on existing records remain
                stored but become invisible. The field can be re-added later with the
                same key to restore visibility.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-zinc-950/40">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 dark:text-white/80 hover:bg-slate-100 dark:hover:bg-white/[0.06]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDelete)}
                data-testid="confirm-delete-button"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white"
              >
                Delete field
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
